import { z } from 'zod'

import { isoDateTime } from '../lib/date'
import { isEmptyHtml } from '../lib/html'
import { labTypeSchema } from './lab'

// ---------------------------------------------------------------------------
// Note types — sole discriminant for both UI badge and content shape
// ---------------------------------------------------------------------------

export const noteTypeSchema = z.enum([
  'soap',
  'clinical',
  'medical_history',
  'internal',
  'follow_up',
  'phone_call',
  'lab_results',
  'external_prescription',
  'general',
])
export type NoteType = z.infer<typeof noteTypeSchema>

// ---------------------------------------------------------------------------
// Note content — JSONB shape depends on `type`
// ---------------------------------------------------------------------------

/** SOAP note content — all four fields optional, all may be empty (draft state). */
export const soapContentSchema = z.object({
  subjective: z.string().optional(),
  objective: z.string().optional(),
  assessment: z.string().optional(),
  plan: z.string().optional(),
})
export type SoapContent = z.infer<typeof soapContentSchema>

/** Free-text note content — used by all note types except soap, follow_up, lab_results, and medical_history. */
export const textContentSchema = z.object({
  text: z.string(),
})
export type TextContent = z.infer<typeof textContentSchema>

/** Lab results note content — separate fields for staff notes and owner-facing communication. */
export const labResultsContentSchema = z.object({
  labType: labTypeSchema,
  clinicalNotes: z.string().optional(),
  clientCommunication: z.string().optional(),
})
export type LabResultsContent = z.infer<typeof labResultsContentSchema>

/**
 * Medical history note content. When records from a previous practice are attached, `summary`
 * is generated from them in the background and is never client-editable: absent with no
 * `summaryError` on a note with a summarisable file means it is still being generated.
 */
export const medicalHistoryContentSchema = z.object({
  text: z.string(),
  /** Vet-facing summary of the attached records, as sanitised rich-text HTML. */
  summary: z.string().optional(),
  /** Why the last generation attempt failed. Cleared when a new attempt starts. */
  summaryError: z.string().optional(),
  /** Set when the note has no file the model can read, so it isn't summarised. */
  summarySkipped: z.boolean().optional(),
  /** ISO time until which one backend process holds the generation; expired means up for grabs. */
  summaryClaimedUntil: z.string().optional(),
})
export type MedicalHistoryContent = z.infer<typeof medicalHistoryContentSchema>

/** File types the summary model reads directly: a medical history note with one gets a summary. */
export function isSummarisableFile(contentType: string): boolean {
  return contentType === 'application/pdf' || contentType.startsWith('image/')
}

/** Follow-up note content — free text plus an optional reference to a linked task. */
export const followUpContentSchema = z.object({
  text: z.string(),
  taskId: z.uuid().optional(),
})
export type FollowUpContent = z.infer<typeof followUpContentSchema>

/** Union of all note content shapes (used for the DB column type). */
export type NoteContent =
  | SoapContent
  | TextContent
  | FollowUpContent
  | LabResultsContent
  | MedicalHistoryContent

// ---------------------------------------------------------------------------
// Discriminated union — { type, content } validated together
// ---------------------------------------------------------------------------

/** All generic note types that share the same { content: { text } } schema. */
const textNoteTypes = [
  'clinical',
  'internal',
  'phone_call',
  'external_prescription',
  'general',
] as const

/**
 * Zod schema that validates a note's `{ type, content }` pair as a discriminated union.
 * Use at the read boundary (DB → API) to ensure content matches the declared type.
 */
export const noteTypeContentSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('soap'), content: soapContentSchema }),
  z.object({ type: z.literal('follow_up'), content: followUpContentSchema }),
  z.object({ type: z.literal('lab_results'), content: labResultsContentSchema }),
  z.object({ type: z.literal('medical_history'), content: medicalHistoryContentSchema }),
  ...textNoteTypes.map((t) => z.object({ type: z.literal(t), content: textContentSchema })),
])

export type NoteTypeContent = z.infer<typeof noteTypeContentSchema>

/** Whether a note of this type holds only `{ text }`, so it can switch to another such type without losing content. */
export function isPlainTextNoteType(type: NoteType): boolean {
  return type === 'medical_history' || (textNoteTypes as readonly NoteType[]).includes(type)
}

/** Human-readable labels for each note type. */
export const NOTE_TYPE_LABELS: Record<NoteType, string> = {
  soap: 'SOAP',
  clinical: 'Clinical',
  medical_history: 'Medical History',
  internal: 'Internal',
  follow_up: 'Follow-up',
  phone_call: 'Phone Call',
  general: 'General',
  lab_results: 'Lab Results',
  external_prescription: 'External Prescription',
}

export const noteSchema = z
  .object({
    id: z.string(),
    patientId: z.string(),
    consultationId: z.string().nullable(),
    createdBy: z.string(),
    /** Staff member credited as the author — defaults to the creator but can be reassigned. */
    writtenBy: z.string(),
    createdAt: isoDateTime,
    updatedAt: isoDateTime,
  })
  .and(noteTypeContentSchema)

/**
 * A note; narrow on `type` to get the correct `content` shape.
 *
 * ```ts
 * if (note.type === 'soap') {
 *   note.content.subjective // SoapContent
 * } else if (note.type === 'follow_up') {
 *   note.content.text       // FollowUpContent (text + optional taskId)
 * } else {
 *   note.content.text       // TextContent
 * }
 * ```
 */
export type Note = z.output<typeof noteSchema>

export type SoapNote = Extract<Note, { type: 'soap' }>
export type FollowUpNote = Extract<Note, { type: 'follow_up' }>
export type TextNote = Extract<Note, { type: (typeof textNoteTypes)[number] }>

/**
 * Check whether a note has any meaningful content worth displaying.
 * Returns true if all content fields are empty or contain only blank HTML.
 */
export function isNoteEmpty(note: NoteTypeContent): boolean {
  const { content } = note

  if (note.type === 'soap') {
    const c = content as {
      subjective?: string
      objective?: string
      assessment?: string
      plan?: string
    }
    return (
      isEmptyHtml(c.subjective) &&
      isEmptyHtml(c.objective) &&
      isEmptyHtml(c.assessment) &&
      isEmptyHtml(c.plan)
    )
  }

  if (note.type === 'lab_results') {
    const c = content as { labType: string; clinicalNotes?: string; clientCommunication?: string }
    return isEmptyHtml(c.clinicalNotes) && isEmptyHtml(c.clientCommunication)
  }

  // TextContent / FollowUpContent — check the text field
  const c = content as { text: string }
  return isEmptyHtml(c.text)
}
